import React from 'react';
import { Globe, Info, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tooltip } from '@mui/material';
import countries from './countries';
import { useTranslation } from '../../i18n/hooks';

interface EnhancementButtonProps {
  needsCountrySelection: boolean;
  onCountrySelect: (country: string) => void;
  selectedCountry: string;
  isLoading: boolean;
  onContinue: () => void;
  disabled: boolean;
  currentStep: string;
}

const EnhancementButton = ({
  needsCountrySelection,
  onCountrySelect,
  selectedCountry,
  isLoading,
  onContinue,
  disabled,
  currentStep
}: EnhancementButtonProps) => {
  const { t } = useTranslation();
  
  if (needsCountrySelection && !selectedCountry) {
    return (
      <div className="flex items-center gap-2">
        <Select onValueChange={onCountrySelect}>
          <SelectTrigger className="min-w-[200px] bg-blue-500 text-white hover:bg-blue-600 border-none">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              <span>{t('enrichmentConfig.select_country_for_image_finder')}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {countries.map((region) => (
              <SelectItem key={region.value} value={region.value}>
                <span className="flex items-center gap-2">
                  {region.flag} {region.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Tooltip 
          title={t('enrichmentConfig.select_country_tooltip')}
          placement="top"
        >
          <Info className="w-4 h-4 text-gray-400 cursor-help" />
        </Tooltip>
      </div>
    );
  }

  return (
    <Button
      size="lg"
      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium"
      onClick={onContinue}
      disabled={disabled}
      data-testid={currentStep === "config" ? "continue-validation-button" : "start-enhancement-button"}
    >
      <div className="flex items-center gap-2">
        {isLoading && <Loader2 className="animate-spin" />}
        <span>
          {currentStep === 'config' ? t('enrichmentConfig.continue_to_verify') : t('enrichmentConfig.start_enhancement')}
        </span>
      </div>
    </Button>
  );
};

export default EnhancementButton;